import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import { Box, Button, List, ListItem, Stack, Typography } from "@mui/material";
import { format } from "date-fns";

import { calculateAge } from "../../../../../../utils/calculateAge";
import { RelationshipType } from "../../../../../api/ClientFileApi";
import { useClientFileContext } from "../../../../../context/ClientFileContext";
import { AssetInventorySection as AssetInventorySubTab } from "../../../../../routes/clientFileRoutes";
import { relationshipLabelKey } from "../../../../utils/relationshipLabels.utils";
import { FieldInfo } from "../field-info/FieldInfo";
import { useMaintenanceObligations } from "./hooks/useMaintenanceObligations";

interface Props {
  clientId: string;
}

export function MaintenanceObligationsBlock({ clientId }: Props) {
  const { t } = useTranslation();
  const { basePaths } = useClientFileContext();
  const { obligations } = useMaintenanceObligations(clientId);

  const countLabel = t("clients.client.debtSettlement.assetInventory.section1.maintenance.countLabel");
  const relationshipLabel = (relationship: RelationshipType | null) =>
    relationship
      ? t(relationshipLabelKey(relationship))
      : t("clients.client.debtSettlement.assetInventory.section1.maintenance.relationshipMissing");

  return (
    <Stack flex={1} p={2} gap={2} sx={{ bgcolor: "background.paper" }}>
      <Typography variant="h6">
        {t("clients.client.debtSettlement.assetInventory.section1.maintenance.title")}
      </Typography>

      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography color="text.secondary">{countLabel}</Typography>
        <FieldInfo
          fieldLabel={countLabel}
          text={t("clients.client.debtSettlement.assetInventory.section1.maintenance.info")}
        />
      </Box>

      {obligations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("clients.client.debtSettlement.assetInventory.section1.maintenance.emptyList")}
        </Typography>
      ) : (
        <Box>
          <Typography fontWeight="bold">
            {t("clients.client.debtSettlement.assetInventory.section1.maintenance.countHeader", {
              count: obligations.length,
            })}
          </Typography>
          <List disablePadding>
            {obligations.map((obligation) => (
              <ListItem key={obligation.id} disableGutters>
                <Typography>
                  {obligation.birthDate
                    ? t("clients.client.debtSettlement.assetInventory.section1.maintenance.entry", {
                        relationship: relationshipLabel(obligation.relationship),
                        birthDate: format(obligation.birthDate, "dd.MM.yyyy"),
                        count: calculateAge(obligation.birthDate, new Date()),
                      })
                    : t("clients.client.debtSettlement.assetInventory.section1.maintenance.entryWithoutBirthDate", {
                        relationship: relationshipLabel(obligation.relationship),
                      })}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Button
        variant="text"
        component={RouterLink}
        to={`${basePaths.assetInventory}/${AssetInventorySubTab.Relatives}`}
        sx={{ alignSelf: "flex-start", px: 0 }}
      >
        {t("clients.client.debtSettlement.assetInventory.section1.maintenance.changeLink")}
      </Button>
    </Stack>
  );
}
